import React, { useCallback, useState, useRef, useEffect } from 'react';
import _ from 'lodash';
import { useCustomCompareEffect } from 'use-custom-compare';

import { LayoutPageHeading, LayoutCardSize } from 'src/modules/layout';
import { ProductEntity } from 'src/modules/products';
import {
  ProductCategoryLocalizedEntity /*, ProductsListingFiltersType*/,
} from 'src/modules/products';
import LayoutPage from 'src/modules/layout/components/page/LayoutPage';
import { ProductsListingStore } from '../../store/types';
import { ProductsListingOverview } from '../..';
import { useLocale, OutputChannels } from 'src/modules/locale';
import styles from './ProductsListingPage.module.scss';
import { FormFilters } from 'src/modules/form/field/filters/FormFilters/FormFilters';
import { Form } from 'antd';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { getQueryFromFilters, getFiltersFromQuery } from 'src/modules/form/field/filters/utils';
import { useMetaTitle } from 'src/utils/ssgHelpers';
import cleanupProducts from 'src/utils/cleanupProducts';

interface ProductsListingPageProps extends ProductsListingStore {
  fetchFiltersData: any;
  dispatch: any;
}

const EMPTY_FILTERS = {
  productSubcategories: [],
  locationTypes: [],
  designers: [],
};

const filterProductsByOutputChannel = (products: any[], outputChannel: OutputChannels) => {
  return _.filter(products, (product) => _.includes(product.channels, outputChannel));
};

/**
 * Save the designers who have products to display them in the filters
 */
const getAvailableDesigners = (products: any[]) => {
  return products
    ? [...new Set(products.map((product) => product.product_series?.designer?.id))]
    : [];
};

/**
 * Save the location types which have products to display them in the filters
 */
const getAvailableLocationTypes = (products: any[]) => {
  return products
    ? [
        ...new Set(
          products.reduce(
            (acc, product) => acc.concat(product.location_types.map((locType: any) => locType.id)),
            []
          )
        ),
      ]
    : [];
};

/**
 * Save the product subcategories which have products to display them in the filters
 */
const getAvailableProductSubcategories = (products: any[]) => {
  return products
    ? [
        ...new Set(
          products.reduce(
            (acc, product) =>
              acc.concat(product.product_subcategories?.map((subcat: any) => subcat)),
            []
          )
        ),
      ]
    : [];
};

const filterProducts = (
  filters: { productSubcategories: any; locationTypes: any; designers: any },
  products: any[]
) => {
  let filteredProducts = [];

  const isSubcategories = (product: ProductEntity) => {
    // No subcategories selected
    if (_.isEmpty(filters.productSubcategories)) return true;

    // One ore more subcategories selected
    return _.some(filters.productSubcategories, (filter) =>
      _.some(product.product_subcategories, (id) => id === filter.id)
    );
  };

  const isDesigners = (product: ProductEntity) => {
    // No designers selected
    if (_.isEmpty(filters.designers)) return true;

    // One ore more designers selected
    return _.some(filters.designers, (filter) => {
      if (!filter?.id || !product?.product_series?.designer?.id) {
        return false;
      }
      return filter?.id === product?.product_series?.designer?.id;
    });
  };

  const isLocationTypes = (product: ProductEntity) => {
    // No location types selected
    if (_.isEmpty(filters.locationTypes)) return true;

    // One ore more location types selected
    return _.some(filters.locationTypes, (filter) =>
      _.some(product.location_types, (locationType) => locationType.id === filter.id)
    );
  };

  filteredProducts = _.filter(
    products,
    (product) => isSubcategories(product) && isDesigners(product) && isLocationTypes(product)
  );

  return filteredProducts;
};

const ProductsListingPage: React.FunctionComponent<ProductsListingPageProps> = (props) => {
  const { getLocalizedObject, outputChannel } = useLocale();
  const router = useRouter();
  const intl = useIntl();
  const [form] = Form.useForm();
  const { productCategory } = props;
  const { filterData, products: localizedProducts } = getLocalizedObject(props);
  const products = filterProductsByOutputChannel(cleanupProducts(localizedProducts), outputChannel);
  const productSubcategories = _.sortBy(filterData?.productSubcategories, (p) => p?.name);
  const designers = _.sortBy(props.filterData?.designers, (p) => p?.name);
  const locationTypes = _.sortBy(filterData?.locationTypes, (p) => p?.name);
  const localizedProductCategory = getLocalizedObject<ProductCategoryLocalizedEntity>(
    productCategory
  );
  const { query } = router;
  const [filteredProducts, setFilteredProducts] = useState(products);

  const [availableFilters, setAvailableFilters] = useState({
    locations: [],
    designers: [],
    subcategories: [],
  });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [category, setCategory] = useState('' as string | string[]);
  const metaTitle = useMetaTitle(localizedProductCategory?.name);
  const queryStringsUpdated = useRef(true);

  // Force redraw since on SSG the content language  doesn't update
  const [forceRedrawKey, setForceRedraKey] = useState('plp-unmounted');
  useEffect(() => {
    setForceRedraKey('plp-mounted');
  }, []);

  let pageProperties: any;
  if (productCategory) {
    pageProperties = {
      title: metaTitle,
      description:
        localizedProductCategory.meta_description || localizedProductCategory.description,

      breadcrumbs: [
        {
          title: intl.formatMessage({
            id: 'breadcrumbs/products',
          }),
          as: '/products',
          href: '/products',
        },
        {
          title: localizedProductCategory.name,
          as: `/products/${encodeURIComponent(productCategory.slug)}`,
          href: '/products/[category]',
        },
      ],
    };
  } else {
    pageProperties = {
      title: '',
      description: '',
    };
  }

  const updateQueryString = useCallback(
    (filterValues) => {
      if (queryStringsUpdated.current) {
        queryStringsUpdated.current = false;
        return;
      }
      const queryFromRouter = { ...router?.query };
      // category is already part of the path, don't add it again to query parameters
      if (queryFromRouter.category) delete queryFromRouter.category;

      const queryFromFilters = getQueryFromFilters(queryFromRouter, filterValues);

      const hasRouterQueries = !_.isEmpty(queryFromRouter);
      const hasFiltersQueries = !_.isEmpty(queryFromFilters);
      const queriesAreDifferent = !_.isEqual(queryFromRouter, queryFromFilters);

      // clear url query
      if (hasRouterQueries && !hasFiltersQueries) {
        router.replace(window.location.pathname, undefined, { shallow: true });
        return;
      }

      // set url query
      if (hasFiltersQueries && queriesAreDifferent) {
        router.push({ query: queryFromFilters }, undefined, { shallow: true });
        return;
      }
    },
    [router]
  );

  const onFiltersChanged = (filterValues: any) => {
    setFilters(filterValues);
  };

  // Query changed
  useCustomCompareEffect(
    () => {
      if (_.isEmpty(filterData)) return;

      const queryFilters = getFiltersFromQuery(filterData, query);
      setFilters(queryFilters);
      setCategory(query.category);
      form.setFieldsValue(queryFilters);
    },
    [filterData, query, form],
    (prevDeps, nextDeps) => _.isEqual([prevDeps[0], prevDeps[1]], [nextDeps[0], nextDeps[1]])
  );

  // Filters changed
  useCustomCompareEffect(
    () => {
      setFilteredProducts(filterProducts(filters, products));

      setAvailableFilters({
        locations: [
          ...getAvailableLocationTypes(filterProducts({ ...filters, locationTypes: [] }, products)),
          ..._.map(filters.locationTypes, (location) => location.id),
        ],
        designers: [
          ...getAvailableDesigners(filterProducts({ ...filters, designers: [] }, products)),
          ..._.map(filters.designers, (designer) => designer.id),
        ],
        subcategories: [
          ...getAvailableProductSubcategories(
            filterProducts({ ...filters, productSubcategories: [] }, products)
          ),
          ..._.map(filters.productSubcategories, (subcat) => subcat.id),
        ],
      });

      updateQueryString(filters);
    },
    [filters, category],
    (prevDeps, nextDeps) => _.isEqual(prevDeps, nextDeps)
  );

  return (
    <LayoutPage {...pageProperties} key={forceRedrawKey}>
      <LayoutPageHeading
        title={localizedProductCategory?.name || ''}
        subline={localizedProductCategory?.description || ''}
      />

      <FormFilters
        className={styles.formFilters}
        resultsCount={_.size(filteredProducts)}
        resultsCountKey="page/products/listing/products_count"
        onChange={onFiltersChanged}
        initialValues={filters}
        form={form}
        filters={{
          productSubcategories: {
            labelKey: 'filters/filter_labels/categories',
            items: productSubcategories.filter((subcat) =>
              availableFilters.subcategories.includes(subcat.id)
            ),
          },
          locationTypes: {
            labelKey: 'filters/filter_labels/rooms',
            items: locationTypes.filter((locationType) =>
              availableFilters.locations.includes(locationType.id)
            ),
          },
          designers: {
            labelKey: 'filters/filter_labels/designers',
            // Display only designers who have products
            items: designers.filter((designer) => availableFilters.designers.includes(designer.id)),
          },
        }}
      />
      {!!filteredProducts.length && (
        <ProductsListingOverview
          oneRow={false}
          products={_.uniqBy(filteredProducts, (product) => product.id)}
          size={LayoutCardSize.Small}
          categorySlug={productCategory?.slug}
        />
      )}
    </LayoutPage>
  );
};

export default ProductsListingPage;
